import { F, T } from '@/helpers'
import {
  api,
  type OVPUserInfo,
  type OVPUserSessionData,
} from '@setplex/tria-api'
import { createEvent, createStore, sample } from 'effector'
import { formatUsername, isAuthenticatedFromInfo } from './helpers'

// Events

export const init = createEvent()
export const updateIsFirstLoginBySocialNetwork = createEvent<boolean>()

// an explicit log out, a failed session check or a 401 while the session was still considered live
export const sessionLost = createEvent()

// Effects

export const checkStatusFx: typeof api.userSession.checkStatusFx =
  api.userSession.checkStatusFx

// Stores

export const $user = createStore<OVPUserSessionData | null>(null)
  .on(checkStatusFx.doneData, (_, data) => data)
  .reset([
    api.user.logOutFx.done,
    checkStatusFx.fail,
    api.events.http.unauthorized,
  ])

export const $info = createStore<OVPUserInfo | null>(null)
  .on(api.userSession.getInfoFx.doneData, (_, data) => data)
  .reset(api.userSession.getInfoFx.fail)

// True for the session a sign in or a sign up opens for the first time. Sign up
// creates the account and the session in one request (OVP-3045), so it answers
// with the same flag the sign in response carries.
export const $isFirstLogin = createStore<boolean>(false)
  .on(api.user.signInFx.doneData, (_, { isFirstLogin }) => isFirstLogin)
  .on(api.user.signUpFx.doneData, (_, { isFirstLogin }) =>
    Boolean(isFirstLogin)
  )
  .reset(api.user.logOutFx.done)

// You can determine login via social networks only by search parameter from BE at home
export const $isFirstLoginBySocialNetwork = createStore<boolean | null>(null) //
  .on(updateIsFirstLoginBySocialNetwork, (_, isFirstLogin) => isFirstLogin)

export const $isAuthenticated = createStore<boolean | null>(null)
  .on(api.userSession.getInfoFx.doneData, (_, info) =>
    isAuthenticatedFromInfo(info)
  )
  .on([api.user.signInFx.done, api.user.signUpFx.done], T)
  .on(
    [api.user.logOutFx.done, checkStatusFx.fail, api.events.http.unauthorized],
    F
  )
  .on(api.userSession.getInfoFx.fail, F)

export const $isNotAuthenticated = $isAuthenticated.map((value) => !value)

// null until the effect resolves
export const $isGuest = $isAuthenticated.map((value) =>
  typeof value === 'object' ? value : !value
)

// `null` until the account request resolves. A session that has not loaded yet is not a user
// without a name, so consumers must not render the `NA` fallback for it.
export const $userInitials = $user.map((user) =>
  user === null
    ? null
    : formatUsername({ firstName: user.firstName, lastName: user.lastName })
)

export const $isSessionGetInfoFxResolved = createStore<boolean>(false).on(
  api.userSession.getInfoFx.finally,
  T
)

// `$isAuthenticated` goes `false` in three unrelated cases: a guest session resolving, a 401 from a
// wrong password, and a session that really was lost. This store tracks a session that exists, so
// that `sessionLost` fires on the last case only.
export const $hasLiveSession = createStore<boolean>(false)
  .on(api.userSession.getInfoFx.doneData, (_, info) =>
    isAuthenticatedFromInfo(info)
  )
  .on([api.user.signInFx.done, api.user.signUpFx.done], T)
  .reset(sessionLost)

// Connections

sample({
  clock: init,
  target: api.userSession.getInfoFx,
})

sample({
  clock: api.userSession.getInfoFx.doneData,
  filter: (info) => info.profile !== null,
  target: checkStatusFx,
})

sample({
  clock: api.user.logOutFx.done,
  target: api.userSession.getInfoFx,
})

// sign up returns a session in the same response, so load the freshly created
// user without a second sign in call
sample({
  clock: api.user.signUpFx.done,
  target: api.userSession.getInfoFx,
})

sample({
  clock: [
    api.user.logOutFx.done,
    checkStatusFx.fail,
    api.events.http.unauthorized,
  ],
  filter: $hasLiveSession,
  target: sessionLost,
})
